import { ApolloClient } from '@apollo/client'
import type { Project, Service } from '../../api'
import { Me, useConsoleContext } from '../Console'
import { LoadingOutlined } from '@ant-design/icons'
import React, { useContext, useEffect, useMemo } from 'react'
import { Route, Routes, useParams } from 'react-router'
import { createApolloClient } from 'admin-web-utils'
import { SimplePage } from '../../components/SimplePage'
import { PageNotFound } from '../../pages'

export interface ProjectState {
  me: Me
  currentProject: Project
  services: Service[]
  projectClient: ApolloClient<unknown>
}

const Context = React.createContext<ProjectState>(undefined as never)

export const useProjectContext = (): ProjectState => useContext(Context)

/**
 * The <ProjectContext> container provides context needed by project-level pages that
 * deal with a particular runtime instance, e.g. Identity Service, Telephony,
 * Virtual Cards, etc.
 *
 * It exposes context such as the selected project and its associated graphql
 * client. For convenience, it also exposes the "me" object from Console context.
 */
export const ProjectContext: React.FC = (props) => {
  const { me, navProject, getAccessToken, setNavProjectId } =
    useConsoleContext()

  const { projectId } = useParams()
  const projectServices = useMemo(
    () => (projectId ? me.projectServices[projectId] : []),
    [projectId, me.projectServices],
  )

  /**
   * Memo:
   * Determine route project and instantiate an API client for it
   */
  const projectInfo = useMemo(() => {
    const routeProject = me.projects.find((project) => project.id === projectId)

    if (!routeProject) {
      // User attempted to access a non-existent project, or they
      // don't have access to the project.
      return false
    }

    return {
      routeProject,
      isNavProjectSynchronized: routeProject === navProject,
      client: createApolloClient(routeProject.apiUri, getAccessToken),
    }
  }, [me, navProject, getAccessToken, projectId])

  /**
   * Effect:
   * If route project and nav project are out of sync, then update
   * nav project on ConsoleContext.
   */
  useEffect(() => {
    if (projectInfo && !projectInfo.isNavProjectSynchronized) {
      setNavProjectId(projectInfo.routeProject.id)
    }
  }, [projectInfo, setNavProjectId])

  return (
    <>
      {!projectInfo ? (
        <Routes>
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      ) : !projectInfo.isNavProjectSynchronized ? (
        <SimplePage>
          <LoadingOutlined />
        </SimplePage>
      ) : (
        <Context.Provider
          value={{
            me,
            currentProject: projectInfo.routeProject,
            projectClient: projectInfo.client,
            services: projectServices,
          }}
        >
          {props.children}
        </Context.Provider>
      )}
    </>
  )
}
